// VendAgain — shared UI helpers: category catalog with icons/colors,
// section labels (Business vs Personal), price formatter, and condition pill.
import {
  ChefHat, Truck, Tent, Wrench, LayoutGrid, PackageSearch,
  Shirt, Sofa, Cpu, Trees, Gem, Sparkles,
} from "lucide-react";

// Category definition = value / label / icon / color pair used across the
// browse grid, filter chips, and detail hero.
export const CATEGORIES = [
  // ----- Business -----
  { value: "restaurant_equipment", label: "Restaurant Equipment", section: "business",
    icon: ChefHat, color: "rgba(196,79,54,0.12)", ink: "var(--terracotta)" },
  { value: "food_trucks_and_trailers", label: "Food Trucks & Trailers", section: "business",
    icon: Truck, color: "rgba(107,47,186,0.12)", ink: "var(--avt-purple)" },
  { value: "event_equipment", label: "Event Equipment", section: "business",
    icon: Tent, color: "rgba(44,196,217,0.16)", ink: "var(--avt-teal)" },
  { value: "tools", label: "Tools", section: "business",
    icon: Wrench, color: "rgba(111,125,85,0.14)", ink: "var(--moss)" },
  { value: "displays_and_fixtures", label: "Displays & Fixtures", section: "business",
    icon: LayoutGrid, color: "rgba(212,171,79,0.16)", ink: "#7a5b12" },
  { value: "supplies", label: "Supplies", section: "business",
    icon: PackageSearch, color: "rgba(75,111,140,0.14)", ink: "#2f4b66" },
  // ----- Personal -----
  { value: "clothing", label: "Clothing", section: "personal",
    icon: Shirt, color: "rgba(236,72,153,0.14)", ink: "var(--avt-pink)" },
  { value: "furniture", label: "Furniture", section: "personal",
    icon: Sofa, color: "rgba(196,79,54,0.12)", ink: "var(--terracotta)" },
  { value: "electronics", label: "Electronics", section: "personal",
    icon: Cpu, color: "rgba(44,196,217,0.16)", ink: "var(--avt-teal)" },
  { value: "home_and_garden", label: "Home & Garden", section: "personal",
    icon: Trees, color: "rgba(111,125,85,0.14)", ink: "var(--moss)" },
  { value: "collectibles", label: "Collectibles", section: "personal",
    icon: Gem, color: "rgba(107,47,186,0.12)", ink: "var(--avt-purple)" },
  { value: "miscellaneous", label: "Miscellaneous", section: "personal",
    icon: Sparkles, color: "rgba(120,90,140,0.14)", ink: "#4a2e63" },
];

export const CAT_MAP = Object.fromEntries(CATEGORIES.map((c) => [c.value, c]));

export const SECTIONS = [
  { key: "business", label: "Business", tagline: "Gear, trucks, fixtures, supplies." },
  { key: "personal", label: "Personal", tagline: "Home items, clothing, collectibles." },
];

export const CONDITIONS = [
  { value: "new", label: "New" },
  { value: "like_new", label: "Like new" },
  { value: "good", label: "Good" },
  { value: "fair", label: "Fair" },
  { value: "for_parts", label: "For parts" },
];

export const CONDITION_MAP = Object.fromEntries(CONDITIONS.map((c) => [c.value, c]));

export function formatPrice(cents, bestOffer) {
  if (bestOffer) return "Best offer";
  const n = Number(cents) || 0;
  if (n === 0) return "Free";
  const dollars = n / 100;
  return dollars % 1 === 0
    ? `$${dollars.toFixed(0)}`
    : `$${dollars.toFixed(2)}`;
}

export function CategoryChip({ value, className = "" }) {
  const c = CAT_MAP[value];
  if (!c) return null;
  const Icon = c.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${className}`}
      style={{ background: c.color, color: c.ink }}
      data-testid={`vendagain-cat-chip-${value}`}
    >
      <Icon size={12} />
      {c.label}
    </span>
  );
}

export function ConditionPill({ value, className = "" }) {
  const c = CONDITION_MAP[value] || { label: value };
  return (
    <span
      className={`inline-flex items-center text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-semibold ${className}`}
      style={{ background: "rgba(0,0,0,0.06)", color: "var(--ink-soft)" }}
      data-testid={`vendagain-cond-pill-${value}`}
    >
      {c.label}
    </span>
  );
}
